/**
 * Mega Level controls (2026-09-03 owner ask): for one Mega form, the level
 * the trainer has reached and a "Mega Evolve now" that starts the eight-hour
 * clock. Every level stays selectable: the Pokémon they evolve may not be
 * their highest-levelled one.
 *
 * The level is picked on GO's own control (owner screenshot, 2026-09-03):
 * a row of helix circles, one per level, the reached ones filled and the
 * current one ringed, plus a greyed first circle for "not yet". The pick is
 * STICKY: it writes the store on the tap and the row re-reads it, so the
 * circles show what was saved, on this page and every other.
 *
 * Used inside the species page's Mega view, the My Pokémon page (where the
 * level is the record's own) and the Mega Evolutions page, so the same tap
 * does the same thing everywhere.
 */
import { useEffect, useState, type ReactNode } from "react";
import { ChevronRight, Clock, Sparkles, Star, Swords, Zap } from "lucide-react";
import { MegaIcon } from "@/components/MegaIcon";
import { MegaProgressBar, MegaRestRing } from "@/components/MegaRings";
import { useMegaMaxLevel } from "@/hooks/useSuperMax";
import { TypeIcon } from "@/components/TypeIcon";
import {
  MEGA_LEVELS,
  megaFormsForSpecies,
  megaLevelInfo,
  megaNamePrefix,
  type MegaFormRef,
  type MegaLevel,
} from "@/lib/megaLevels";
import {
  activateMega,
  clearActiveMega,
  setMegaLevel,
  useActiveMega,
  useMegaLevels,
} from "@/lib/megaLevelStore";
import { megaFormSpriteCandidates, megaGmaxSpriteCandidates, MEGA_EXTRA_FORMS } from "@/lib/megaGmaxSprites";
import { megaEnergyForFormKey, TRAINER_RESOURCES_EVENT } from "@/lib/trainerResources";
import { isPokeType, typesFor, type PokeType } from "@/lib/types";

export const MEGA_COLOR = "hsl(var(--mega))";

/** "5h 12m" left on an active Mega. */
export function megaTimeLeft(expiresAt: number, now = Date.now()): string {
  const ms = Math.max(0, expiresAt - now);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Art for a Mega form (shiny on request), stepping through the candidate URLs on error. */
export function MegaSprite({ form, size = 56, shiny = false }: { form: MegaFormRef; size?: number; shiny?: boolean }) {
  const candidates = form.formId
    ? megaFormSpriteCandidates(MEGA_EXTRA_FORMS.find((f) => f.formId === form.formId)!, { shiny })
    : megaGmaxSpriteCandidates("mega", form.dexId, { shiny });
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [form.key, shiny]);
  const src = candidates[i];
  if (!src) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full"
        style={{ width: size, height: size, background: "hsl(var(--mega) / 0.12)", color: MEGA_COLOR }}
        aria-hidden
      >
        <MegaIcon size={Math.round(size * 0.5)} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={shiny ? `Shiny ${form.name}` : form.name}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setI((n) => n + 1)}
      style={{ width: size, height: size, objectFit: "contain" }}
      data-shiny={shiny ? "true" : undefined}
    />
  );
}

/**
 * The Mega Energy the trainer holds for one form, live: the scan and the
 * screenshot upload both write trainerResources, and this re-reads on its
 * event, so a figure read a moment ago shows without leaving the page.
 */
export function useMegaEnergy(form: Pick<MegaFormRef, "key">): { energy: number; updatedAt: number } | null {
  const read = () => megaEnergyForFormKey(form.key);
  const [energy, setEnergy] = useState(read);
  useEffect(() => {
    setEnergy(read());
    const on = () => setEnergy(read());
    window.addEventListener(TRAINER_RESOURCES_EVENT, on);
    return () => window.removeEventListener(TRAINER_RESOURCES_EVENT, on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.key]);
  return energy;
}

/**
 * The types a Mega shares its catch-Candy bonus with: the Mega form's own
 * from the stats feed (Mega Charizard X is Fire / Dragon), falling back to
 * the species' types for a Mega the feed has no row for yet.
 */
export function megaCatchTypes(form: MegaFormRef): PokeType[] {
  const fromStat = (form.stat?.types ?? []).map((t) => t.toLowerCase()).filter(isPokeType);
  return fromStat.length > 0 ? fromStat : typesFor(form.dexId);
}

/**
 * The gold level pills: Not yet / Base / High / Max / Super Max. The
 * Bonuses page's active Mega card keeps these (owner call 2026-09-03: the
 * pills read better there than the circles); the species and My Pokémon
 * pages use the circles below.
 */
export function MegaLevelChips({
  value,
  onChange,
  testIdPrefix,
}: {
  value: MegaLevel | null;
  onChange: (level: MegaLevel | null) => void;
  testIdPrefix: string;
}) {
  const chip = (label: string, level: MegaLevel | null) => {
    const active = value === level;
    return (
      <button
        key={label}
        type="button"
        onClick={() => onChange(level)}
        aria-pressed={active}
        data-testid={`${testIdPrefix}-${level ?? "none"}`}
        className="rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
        style={{
          background: active ? "hsl(var(--mega) / 0.18)" : "var(--surface-sunken)",
          border: `1px solid ${active ? MEGA_COLOR : "var(--border-subtle)"}`,
          color: active ? MEGA_COLOR : "var(--text-primary)",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {chip("Not yet", null)}
      {MEGA_LEVELS.map((l) => chip(l.label, l.level))}
    </div>
  );
}

/**
 * The level circles: Base / High / Max / Super Max, GO's own four (owner
 * call 2026-09-03: no "Not yet" circle, four greyed circles already read as
 * not yet). Every circle up to the reached level fills; the reached one is
 * ringed; tapping the reached one again takes the level away. Labels sit
 * under each circle.
 */
export function MegaLevelCircles({
  value,
  onChange,
  testIdPrefix,
  size = 34,
  maxLevel = 4,
}: {
  value: MegaLevel | null;
  onChange: (level: MegaLevel | null) => void;
  testIdPrefix: string;
  size?: number;
  /** 3 until Super Max is released for the species, then 4 (owner call 2026-09-03). */
  maxLevel?: 3 | 4;
}) {
  const circle = (label: string, level: MegaLevel) => {
    const reached = value != null && level <= value;
    const current = value === level;
    return (
      <div key={label} className="flex flex-col items-center gap-1 shrink-0" style={{ width: Math.max(size + 14, 46) }}>
        <button
          type="button"
          onClick={() => onChange(current ? null : level)}
          aria-pressed={current}
          aria-label={current ? `${label} level reached. Tap to clear.` : `${label} level`}
          title={current ? `${label} level. Tap again to clear.` : `${label} level`}
          data-testid={`${testIdPrefix}-${level}`}
          data-reached={reached ? "true" : undefined}
          className="rounded-full grid place-items-center transition-transform active:scale-95"
          style={{
            width: size,
            height: size,
            background: reached ? MEGA_COLOR : "var(--surface-sunken)",
            border: `1px solid ${reached ? MEGA_COLOR : "var(--border-subtle)"}`,
            boxShadow: current
              ? "0 0 0 2px var(--surface-raised, var(--surface-sunken)), 0 0 0 4px hsl(24 92% 58%)"
              : "none",
          }}
        >
          <MegaIcon
            size={Math.round(size * 0.55)}
            style={{ color: reached ? "var(--text-on-accent)" : "var(--text-tertiary)" }}
          />
        </button>
        <span
          aria-hidden
          className="text-[8px] font-bold leading-[1.1] text-center uppercase tracking-wide"
          style={{ color: current ? MEGA_COLOR : "var(--text-tertiary)" }}
        >
          {label}
        </span>
      </div>
    );
  };
  return (
    <div className="flex items-start justify-around gap-1" data-testid={`${testIdPrefix}-row`} data-level={value ?? "none"} data-max-level={maxLevel}>
      {MEGA_LEVELS.filter((l) => l.level <= maxLevel).map((l) => circle(l.label, l.level))}
    </div>
  );
}

/**
 * The bonuses a level grants, drawn the way GO lists them: an icon per line.
 * The catch-Candy line shows the types it applies to as the game's type
 * icons, so it is clear which catches earn the extra Candy.
 */
export function MegaPerkList({
  level,
  types,
  testId,
}: {
  level: MegaLevel;
  types: readonly PokeType[];
  testId?: string;
}) {
  const p = megaLevelInfo(level);
  const rest = p.restHours >= 48 ? `${p.restHours / 24}-day` : `${p.restHours}-hour`;
  const rows: Array<{ key: string; icon: ReactNode; text: ReactNode }> = [
    {
      key: "candy",
      icon: <Sparkles className="w-3.5 h-3.5" />,
      text: (
        <span className="inline-flex flex-wrap items-center gap-1">
          +{p.catchCandy} Candy per catch of
          {types.length > 0 ? (
            <span className="inline-flex items-center gap-0.5" data-testid={`${testId ?? "mega-perk"}-types`}>
              {types.map((t) => (
                <TypeIcon key={t} type={t} size={16} />
              ))}
            </span>
          ) : (
            " a Pokémon sharing its type"
          )}
        </span>
      ),
    },
    { key: "attack", icon: <Swords className="w-3.5 h-3.5" />, text: "Boosted attacks for every Pokémon in the raid, more for those sharing its type" },
  ];
  if (p.xlChancePct > 0) rows.push({ key: "xl", icon: <Star className="w-3.5 h-3.5" />, text: `+${p.xlChancePct}% chance of Candy XL on those catches` });
  if (p.catchXp > 0) rows.push({ key: "xp", icon: <Zap className="w-3.5 h-3.5" />, text: `+${p.catchXp} XP on those catches` });
  rows.push({ key: "rest", icon: <Clock className="w-3.5 h-3.5" />, text: `${rest} Mega Evolution rest period` });
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.key} className="flex items-start gap-2">
          <span
            className="mt-px inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full"
            style={{ color: MEGA_COLOR, background: "hsl(var(--mega) / 0.12)" }}
          >
            {r.icon}
          </span>
          <span className="text-[12px] leading-snug" style={{ color: "var(--text-primary)" }}>
            {r.text}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * One form's controls: its art and name, the level circles, the energy known
 * against the cost, the level's bonuses, and the evolve button (or the
 * running clock). The level comes from the species-wide store unless the
 * caller owns it (the My Pokémon page passes the record's own).
 */
export function MegaFormLevelControls({
  form,
  compact = false,
  level: ownedLevel,
  onLevelChange,
  showPerks = !compact,
  testIdPrefix = "mega",
  yours,
}: {
  form: MegaFormRef;
  compact?: boolean;
  /** Override the store's level with the caller's (a single Pokémon's). */
  level?: MegaLevel | null;
  onLevelChange?: (level: MegaLevel | null) => void;
  showPerks?: boolean;
  /** Leading segment of every test id in the block ("mega" by default). */
  testIdPrefix?: string;
  /**
   * The trainer's own Pokémon of the species (2026-10-07 owner ask). When
   * given, the card always draws the rest ring (an unread rest shows as
   * "Free?", not nothing) and links to those Pokémon in My Pokémon, since a
   * rest, and so a confident "Free", is only ever read off a scanned one.
   */
  yours?: MegaYoursLinkProps;
}) {
  const tid = (s: string) => `${testIdPrefix}-${s}`;
  const maxLevel = useMegaMaxLevel(form.dexId);
  const levels = useMegaLevels();
  const active = useActiveMega();
  const entry = levels[form.key];
  const level = ownedLevel !== undefined ? ownedLevel : entry?.level ?? null;
  const isActive = active?.formKey === form.key;
  // A minute tick keeps the eight-hour clock and the rest countdown moving.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isActive && !entry?.restEndsAt) return;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [isActive, entry?.restEndsAt]);

  const pick = (l: MegaLevel | null) => {
    if (onLevelChange) onLevelChange(l);
    else setMegaLevel(form.key, l);
  };

  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: isActive ? "hsl(var(--mega) / 0.10)" : "var(--surface-raised)",
        border: `1px solid ${isActive ? MEGA_COLOR : "var(--border-subtle)"}`,
      }}
      data-testid={tid(`form-${form.key}`)}
      data-active={isActive ? "true" : undefined}
      data-level={level ?? "none"}
    >
      {/* Header: art, name and level on the left; the rest ring (when one
          is running) on the right, beside the form. The energy ring that sat
          there came off on 2026-09-04 (owner direction). */}
      <div className="flex items-center gap-3">
        <MegaSprite form={form} size={compact ? 44 : 56} />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold truncate" style={{ color: "var(--text-primary)" }}>
            {form.name}
          </p>
          <p className="text-[10.5px]" style={{ color: level != null ? MEGA_COLOR : "var(--text-tertiary)" }}>
            {level == null ? "Not yet Mega Evolved" : `${megaLevelInfo(level).label} level`}
          </p>
          {isActive && active && (
            <span
              className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5"
              style={{ background: "hsl(var(--mega) / 0.18)", color: MEGA_COLOR }}
              data-testid={tid(`form-active-${form.key}`)}
            >
              {megaTimeLeft(active.expiresAt, now)} left
            </span>
          )}
        </div>
        <div className="flex items-start gap-3 shrink-0">
          {(entry?.restEndsAt || isActive || yours) && (
            <MegaRestRing entry={entry} now={now} size={compact ? 44 : 52} testId={tid(`rest-${form.key}`)} />
          )}
          {/* The energy ring came off this card (owner direction 2026-09-04);
              the Mega Evolutions page still draws it per form. */}
        </div>
      </div>
      {yours && (
        <div className="mt-2.5">
          <MegaYoursLink {...yours} testId={tid(`yours-${form.key}`)} />
        </div>
      )}
      <div className="mt-3">
        <MegaLevelCircles
          value={level}
          onChange={pick}
          testIdPrefix={tid(`level-${form.key}`)}
          size={compact ? 30 : 36}
          maxLevel={maxLevel}
        />
      </div>
      {level != null && (
        <div className="mt-2.5">
          <MegaProgressBar level={level} entry={entry} testId={tid(`progress-${form.key}`)} />
        </div>
      )}

      {showPerks && level != null && (
        <div className="mt-3">
          <p className="text-[9px] font-black uppercase tracking-[0.16em] mb-1.5" style={{ color: "var(--text-tertiary)" }}>
            {megaNamePrefix(form.dexId) === "Primal" ? "Primal Reversion bonuses" : "Mega Evolution bonuses"}
          </p>
          <MegaPerkList level={level} types={megaCatchTypes(form)} testId={tid(`perks-${form.key}`)} />
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        {isActive ? (
          <button
            type="button"
            onClick={() => clearActiveMega()}
            data-testid={tid(`end-${form.key}`)}
            className="rounded-full px-3 py-1.5 text-[11px] font-semibold"
            style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)" }}
          >
            Ended early
          </button>
        ) : (
          <button
            type="button"
            onClick={() => activateMega(form.key, level ?? 1)}
            data-testid={tid(`evolve-${form.key}`)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold"
            style={{ background: MEGA_COLOR, color: "var(--text-on-accent)" }}
          >
            <Zap className="w-3.5 h-3.5" />
            Mega Evolve
          </button>
        )}
        <span className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
          {isActive ? "Its bonuses are on the Bonuses page." : "Mega Evolve to show its bonuses in active bonuses."}
        </span>
      </div>
    </div>
  );
}

export interface MegaYoursLinkProps {
  /** How many of the species the trainer has in My Pokémon. */
  copies: number;
  /** Opens My Pokémon on those Pokémon (or on the page, to scan one). */
  onOpen: () => void;
}

/**
 * The link from a Mega card to the trainer's own Pokémon of the species
 * (2026-10-07 owner ask). A rest, and so the "Free to evolve" ring, is only
 * ever read off a scanned Pokémon, so this says where that reading comes
 * from and takes the trainer there: to their copies when they have some, or
 * to My Pokémon to scan one when they have none.
 */
export function MegaYoursLink({ copies, onOpen, testId = "mega-yours" }: MegaYoursLinkProps & { testId?: string }) {
  const some = copies > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      data-copies={copies}
      className="inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1.5 py-1 text-[10.5px] font-bold"
      style={{
        background: "var(--surface-sunken)",
        border: `1px solid ${some ? "var(--accent-fill-glow-subtle, var(--border-subtle))" : "var(--border-subtle)"}`,
        color: some ? "var(--text-primary)" : "var(--text-tertiary)",
      }}
    >
      {some ? (
        <>
          <span className="tabular-nums" style={{ color: "var(--accent-fill)" }}>{copies}</span>
          <span>in My Pokémon</span>
        </>
      ) : (
        <span>Scan one of yours to read its rest</span>
      )}
      <ChevronRight className="w-3.5 h-3.5" style={{ color: "var(--text-tertiary)" }} />
    </button>
  );
}

/** Every Mega form of a species, with controls. Renders nothing for a species without a Mega. */
export function MegaFormsPanel({ dexId, compact = false }: { dexId: number; compact?: boolean }) {
  const forms = megaFormsForSpecies(dexId);
  if (forms.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={`mega-forms-panel-${dexId}`}>
      {forms.map((f) => (
        <MegaFormLevelControls key={f.key} form={f} compact={compact} />
      ))}
    </div>
  );
}
